import { getYear, parseISO } from 'date-fns';
import { addDays, isAfter, nextSunday, subDays } from 'date-fns/fp';
import { firstBy, identity, map, pipe, sort } from 'remeda';
import { createContext } from 'react';

import { COUNTRIES, isCountryCode, ZoneCode, ZONES } from '@/web/components/countries';
import { Data, Event, Weekend, Zone } from '@/web/components/types';
import { thisYear } from '@/web/components/common';
import type { Output } from '@/common/output';

export const DataContext = createContext<Data>(inputToData({ people: [], events: [], countries: [] }));

export function inputToData(input: Output): Data {
	const now = new Date();
	const events: Event[] = [];
	const weekends = new Map<number, Weekend>();
	let firstYear = thisYear;
	let lastYear = thisYear;

	const zoneMap: Map<ZoneCode, Zone> = new Map();

	for (const inputEvent of input.events) {
		const sunday = parseISO(inputEvent.sunday);
		const countryCode = isCountryCode(inputEvent.country_code) ? inputEvent.country_code : 'unknown';
		const { zoneCode, name: countryName } = COUNTRIES[countryCode];
		let zone = zoneMap.get(zoneCode);
		if (!zone) {
			zoneMap.set(zoneCode, zone = { code: zoneCode, name: ZONES[zoneCode].name, countries: [], eventCount: 1 });
		} else {
			zone.eventCount++;
		}
		let country = zone.countries.find(c => c.code === countryCode);
		if (!country) {
			zone.countries.push(country = { name: countryName, code: countryCode, zone, eventCount: 1 });
		} else {
			country.eventCount++;
		}

		const event: Event = {
			name: inputEvent.name,
			cover: inputEvent.cover,
			facebook: inputEvent.facebook ?? {},
			ydb: inputEvent.ydb,
			westiehub: inputEvent.westiehub,
			dancecircuit: inputEvent.dancecircuit,
			links: inputEvent.links ?? [],
			people: inputEvent.people ?? [],
			venues: inputEvent.venues ?? {},
			sunday,
			country,
			hotel: inputEvent.hotel ?? false,
			wsdc: inputEvent.wsdc ?? false,
			cancelled: inputEvent.cancelled ?? false,
			registration: isAfter(subDays(2, sunday), now) ? undefined : inputEvent.registration,
			hasVideos: (inputEvent.links ?? []).some(({ kind }) => kind === 'videos'),
			hasResults: (inputEvent.links ?? []).some(({ kind }) => kind === 'results'),
		};

		events.push(event);

		const ws = weekends.get(sunday.getTime()) || { sunday, events: [] };
		ws.events.push(event);
		weekends.set(sunday.getTime(), ws);

		const year = getYear(sunday);
		if (year > lastYear) lastYear = year;
		if (year < firstYear) firstYear = year;
	}

	for (const ws of weekends.values()) {
		ws.events = sortEvents(ws.events);
	}

	const zones = [...zoneMap.values()];
	sort(zones, (z1, z2) => z2.eventCount - z1.eventCount);
	for (const zone of zoneMap.values()) {
		sort(zone.countries, (c1, c2) => c2.eventCount - c1.eventCount);
	}

	const countries = [...zoneMap.values()].flatMap(z => z.countries)
	const regions = {
		countries,
		countryMap: new Map(countries.map(c => [c.code, c])),
		zones,
		zoneMap: new Map(zones.map(z => [z.code, z])),
	};

	return {
		now,
		events,
		weekends,
		people: input.people,
		regions,
		firstYear,
		lastYear,
	};
}

export function sundaysForYear(year: number): Date[] {
	const sundays = [];
	const first = pipe(new Date(year, 0, 1), subDays(13), nextSunday);
	const last = pipe(new Date(year, 11, 31), addDays(6), nextSunday);
	for (let sunday = first; !isAfter(last, sunday); sunday = nextSunday(sunday)) {
		sundays.push(sunday);
	}
	return sundays;
}

function sortEvents(events: Event[]): Event[] {
	return events.sort((e1, e2) => {
		const going1 = firstBy([0,
			...pipe(e1.facebook, Object.values, map(i => i.going)),
			...pipe(e1.facebook, Object.values, map(i => i.responded)),
		], [identity, 'desc']);
		const going2 = firstBy([0,
			...pipe(e2.facebook, Object.values, map(i => i.going)),
			...pipe(e2.facebook, Object.values, map(i => i.responded)),
		], [identity, 'desc']);
		if (going1 > going2) { return -1; }
		if (going1 < going2) { return 1; }
		return e1.name > e2.name ? 1 : -1;
	});
}
